import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { BlogPostCard } from '@/components/blog/public/BlogPostCard';
import { BlogPagination } from '@/components/blog/public/BlogPagination';
import { usePublicBlogPosts } from '@/hooks/blog/usePublicBlogPosts';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReadingProgressBar } from '@/components/ui/reading-progress-bar';
import { Skeleton } from '@/components/ui/skeleton';

type TagCategory = {
  name: string;
  displayName: string;
  description?: string;
};

type Tag = {
  id: string;
  name: string;
  category: string;
};

export default function PublicBlogListing() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const renderCount = useRef(0);
  const mountRef = useRef(false);
  
  useEffect(() => {
    renderCount.current += 1;
    
    if (!mountRef.current) {
      console.log('[DEBUG] PublicBlogListing MOUNTED');
      mountRef.current = true;
    }
    
    console.log(`[DEBUG] PublicBlogListing RENDER #${renderCount.current}`);
    console.log(`[DEBUG] PublicBlogListing location: ${location.pathname}${location.search}`);
  });
  
  const searchParams = new URLSearchParams(location.search);
  const tagFromUrl = searchParams.get('tag');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined);
  const [activeTag, setActiveTag] = useState<string | undefined>(tagFromUrl || undefined);
  const [categories, setCategories] = useState<TagCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  
  console.log(`[DEBUG] PublicBlogListing state - tag: ${activeTag}, category: ${activeCategory}, page: ${currentPage}`);
  
  const { posts, loading, totalPages } = usePublicBlogPosts({
    page: currentPage,
    limit: 9,
    tag: activeTag,
    category: activeCategory
  });
  
  console.log(`[DEBUG] PublicBlogListing after usePublicBlogPosts - posts: ${posts.length}, loading: ${loading}, totalPages: ${totalPages}`);
  
  const isLoading = loading || loadingTags || isPageTransitioning;

  useEffect(() => {
    console.log(`[DEBUG] PublicBlogListing URL effect - tag: ${activeTag}`);
    const params = new URLSearchParams(location.search);
    
    if (activeTag) {
      params.set('tag', activeTag);
    } else {
      params.delete('tag');
    }
    
    if (params.toString() !== location.search.replace(/^\?/, '')) {
      console.log(`[DEBUG] PublicBlogListing updating URL from ${location.search} to ?${params.toString()}`);
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [activeTag, navigate, location.search]);

  useEffect(() => {
    const effectId = Math.random().toString(36).substr(2, 9);
    console.log(`[DEBUG] PublicBlogListing tags effect ${effectId} STARTED`);
    
    const fetchTags = async () => {
      setLoadingTags(true);
      
      try {
        console.log(`[DEBUG] PublicBlogListing tags effect ${effectId} - Fetching tags for blog listings...`);
        const { data: tagData, error: tagError } = await supabase
          .from('tags')
          .select('id, name, category')
          .order('name');
          
        if (tagError) {
          console.error(`[DEBUG] PublicBlogListing tags effect ${effectId} - Error fetching tags:`, tagError);
          throw tagError;
        }
        
        const uniqueCategories = [...new Set(tagData?.map(tag => tag.category) || [])];
        
        const categoryObjects: TagCategory[] = uniqueCategories.map(category => ({
          name: category || 'uncategorized',
          displayName: formatCategoryName(category || 'uncategorized')
        }));
        
        categoryObjects.sort((a, b) => a.displayName.localeCompare(b.displayName));
        
        categoryObjects.unshift({
          name: 'all',
          displayName: 'All Posts'
        });
        
        console.log(`[DEBUG] PublicBlogListing tags effect ${effectId} - Found ${categoryObjects.length} categories and ${tagData?.length || 0} tags`);
        
        setCategories(categoryObjects);
        setTags(tagData || []);
      } catch (err) {
        console.error(`[DEBUG] PublicBlogListing tags effect ${effectId} - Error fetching tags:`, err);
      } finally {
        setLoadingTags(false);
      }
    };
    
    fetchTags();
    
    return () => {
      console.log(`[DEBUG] PublicBlogListing tags effect ${effectId} CLEANUP`);
    };
  }, []);

  const formatCategoryName = (name: string): string => {
    return name.charAt(0).toUpperCase() + name.slice(1);
  };
  
  const getTagsForCategory = (category: string): Tag[] => {
    if (category === 'all') return [];
    return tags.filter(tag => tag.category === category);
  };

  const handlePageChange = (page: number) => {
    console.log(`[DEBUG] PublicBlogListing - Changing to page ${page}`);
    setIsPageTransitioning(true);
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setTimeout(() => {
      setIsPageTransitioning(false);
    }, 500);
  };

  const handleCategoryChange = (value: string) => {
    console.log(`[DEBUG] PublicBlogListing - Changing category to ${value}`);
    setIsPageTransitioning(true);
    const category = value === "all" ? undefined : value;
    setActiveCategory(category);
    setActiveTag(undefined); // Clear active tag when changing category
    setCurrentPage(1);
    
    setTimeout(() => {
      setIsPageTransitioning(false);
    }, 500);
  };

  const handleTagChange = (tag: string | undefined) => {
    console.log(`[DEBUG] PublicBlogListing - Changing tag to: "${tag}"`);
    setIsPageTransitioning(true);
    setActiveTag(tag);
    setActiveCategory(undefined); // Clear active category when selecting specific tag
    setCurrentPage(1);
    
    setTimeout(() => {
      setIsPageTransitioning(false);
    }, 500);
  };

  useEffect(() => {
    return () => {
      console.log('[DEBUG] PublicBlogListing UNMOUNTED');
    };
  }, []);

  return (
    <>
      <ReadingProgressBar isLoading={isLoading} />
      
      <Helmet>
        <title>Travel Blog | Trotting the World</title>
        <meta name="description" content="Wonders Unfurled! Explore travel tips, guides, and stories from around the world. Plan your perfect adventure with insights from experienced travelers." />
        <meta property="og:title" content="Travel Blog | Trotting the World" />
        <meta property="og:description" content="Wonders Unfurled! Explore travel tips, guides, and stories from around the world. Plan your perfect adventure with insights from experienced travelers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/blog" />
      </Helmet>

      <header className="mb-10 text-center">
        <h1 className="text-4xl font-bold tracking-tight mb-4">Travel Blog</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Explore travel tips, destination guides, and adventure stories from around the world.
        </p>
      </header>

      <div className="mb-4 overflow-x-auto pb-2">
        <TooltipProvider delayDuration={300}>
          <Tabs value={activeCategory || "all"} onValueChange={handleCategoryChange}>
            <TabsList className="flex flex-nowrap min-w-max mx-auto">
              {categories.map((category) => (
                <Tooltip key={category.name}>
                  <TooltipTrigger asChild>
                    <TabsTrigger value={category.name}>
                      {category.displayName}
                    </TabsTrigger>
                  </TooltipTrigger>
                  {category.description && (
                    <TooltipContent>
                      <p>{category.description}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </TabsList>
          </Tabs>
        </TooltipProvider>
      </div>

      {activeCategory && (
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => handleTagChange(undefined)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                activeTag === undefined 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              All {formatCategoryName(activeCategory)} Posts
            </button>
            
            {getTagsForCategory(activeCategory).map((tag) => (
              <button
                key={tag.id}
                onClick={() => {
                  console.log(`[DEBUG] Tag button clicked: "${tag.name}"`);
                  handleTagChange(tag.name);
                }}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  activeTag === tag.name 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 animate-fade-in">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="overflow-hidden rounded-lg border border-border">
              <Skeleton className="h-48 w-full" />
              <div className="p-4 space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <div className="flex gap-2 mt-4">
                  <Skeleton className="h-8 w-20 rounded-full" />
                  <Skeleton className="h-8 w-20 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : posts.length > 0 ? (
        <>
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 transition-opacity duration-300 ${isPageTransitioning ? 'opacity-70' : 'opacity-100'}`}>
            {posts.map((post) => (
              <BlogPostCard key={post.id} post={post} />
            ))}
          </div>
          <BlogPagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={handlePageChange}
          />
        </>
      ) : (
        <div className="text-center py-20">
          <h3 className="text-xl font-medium mb-2">No blog posts found</h3>
          <p className="text-muted-foreground">
            {activeTag 
              ? `There are no published posts with the tag "${activeTag}" yet.`
              : activeCategory
                ? `There are no published posts in the "${formatCategoryName(activeCategory)}" category yet.`
                : "There are no published blog posts yet."}
          </p>
        </div>
      )}
    </>
  );
}
